import React, { Fragment, useContext } from "react";
import { Check, ChevronDown } from "lucide-react";
import { CMSModal } from "@/context";
import { Menu, Transition } from "@headlessui/react";
import { twMerge } from "tailwind-merge";

type Props = {
  value: string;
  onChange: (newStatus: string) => void;
};

const STATUS_OPTIONS = [
  { label: "In", value: "In", color: "bg-green-500" },
  { label: "Out", value: "Out", color: "bg-gray-400" },
  { label: "Remote", value: "Remote", color: "bg-[#099ed1]" },
];

const ColorDot = ({ color }: { color: string }) => (
  <span className={twMerge("h-2.5 w-2.5 rounded-full", color)} />
);

const EmployeeStatus: React.FC<Props> = ({ value, onChange }) => {
  const { theme } = useContext(CMSModal);

  const selected =
    STATUS_OPTIONS.find((o) => o.value === value) || STATUS_OPTIONS[0];

  return (
    <Menu as="div" className="relative w-24 bottom-2">
      <Menu.Button
        className={twMerge(
          "flex w-full items-center justify-between rounded-full px-3 py-[3px] text-xs text-white",
          selected.color,
        )}
      >
        <span>{selected.label}</span>
        <ChevronDown className="h-4 w-4 text-white" />
      </Menu.Button>

      <Transition
        as={Fragment}
        enter="transition ease-out duration-100"
        enterFrom="opacity-0 scale-95"
        enterTo="opacity-100 scale-100"
        leave="transition ease-in duration-75"
        leaveFrom="opacity-100 scale-100"
        leaveTo="opacity-0 scale-95"
      >
        <Menu.Items
          className={twMerge(
            "absolute z-50 mt-1 w-full rounded-md shadow-lg ring-1 focus:outline-none",
            theme === "dark"
              ? "bg-[#262727] ring-white/10 border border-gray-700"
              : "bg-white ring-black/5",
          )}
        >
          <div className="py-1">
            {STATUS_OPTIONS.map((opt) => {
              const isSelected = value === opt.value;

              return (
                <Menu.Item key={opt.value}>
                  {({ active }) => (
                    <button
                      type="button"
                      disabled={isSelected}
                      onClick={() => !isSelected && onChange(opt.value)}
                      className={twMerge(
                        "flex w-full items-center justify-between px-3 py-1 text-xs",
                        active &&
                          !isSelected &&
                          (theme === "dark" ? "bg-gray-800" : "bg-gray-100"),
                        isSelected &&
                          "font-medium opacity-60 cursor-not-allowed",
                      )}
                    >
                      <div className="flex items-center gap-2">
                        <ColorDot color={opt.color} />
                        <span>{opt.label}</span>
                      </div>

                      {isSelected && (
                        <Check className="h-4 w-4 text-blue-500" />
                      )}
                    </button>
                  )}
                </Menu.Item>
              );
            })}
          </div>
        </Menu.Items>
      </Transition>
    </Menu>
  );
};

export default EmployeeStatus;
